import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { RbacAdminService } from 'src/app/services/rbac-admin/rbac-admin.service';
import { UserService } from 'src/app/services/user/user.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import * as alertifyjs from 'alertifyjs';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  isValidPassword,
  isValidPhoneVE,
  isValidUsername,
  isValidEmail,
  sanitizePhone,
  VALIDATION_MESSAGES,
  DEACTIVATION_REASONS
} from 'src/app/utils/form-validators';

@Component({
  selector: 'app-roles-permisos',
  templateUrl: './roles-permisos.component.html',
  styleUrls: ['./roles-permisos.component.css']
})
export class RolesPermisosComponent implements OnInit, OnDestroy {

  @HostListener('document:click')
  onDocumentClick() {
    this.closeAllMenus();
  }

  private searchSubject = new Subject<string>();
  private searchSubscription: any;

  loading = true;
  loadingUsers = false;
  saving = false;

  // ── Tabs ──
  activeTab: 'dashboard' | 'users' | 'departments' | 'roles' | 'permissions' | 'audit' = 'dashboard';

  // ---- Permisos especiales por usuario (modal Detalle de Usuario; no van por rol) ----
  userDetailTarget: any = null;
  individualCatalog: { code: string; name: string; description: string; riskLevel: string }[] = [];
  individualGrants = new Set<string>();        // `${userId}|${code}`
  loadingIndividual = false;
  savingIndividual: string | null = null;       // `${userId}|${code}` en curso

  // ── Roles (desde backend) ──
  roles: any[] = [];

  // ── Users (desde backend) ──
  users: any[] = [];
  usersTotal = 0;
  usersPage = 1;
  usersLimit = 15;
  searchTerm = '';
  filterRole = '';
  filterDepartment = '';
  filterLevel = '';
  usersTotalPages = 1;
  statusFilter: 'active' | 'inactive' = 'active';

  // ── Ordenamiento de tabla de usuarios (client-side, sobre la página visible) ──
  usersSortColumn: 'username' | 'name' | 'email' | 'role' | null = null;
  usersSortDirection: 'asc' | 'desc' = 'asc';

  // ── Modal de asignación (multi-rol, modo batch) ──
  showAssignModal = false;
  selectedUser: any = null;
  selectedRoleCode = '';
  stagedRoles: any[] = [];
  originalRoleCodes: string[] = [];

  // ── Modal de detalle de usuario (roles + permisos, solo lectura) ──
  showUserDetailModal = false;
  userDetailData: any = null;
  loadingUserDetail = false;

  // ── Departamentos ──
  departmentsList: any[] = [];
  loadingDepartments = false;
  newDepartmentName = '';
  savingDepartment = false;
  deletingDepartment = '';
  showCreateDeptModal = false;
  showAssignRoleToDeptModal = false;
  assignRoleToDeptTarget: any = null;
  assignRoleToDeptCode = '';

  // ── Modal de usuarios de un rol ──
  showRoleUsersModal = false;
  roleUsersData: any = null;
  roleUsersList: any[] = [];
  loadingRoleUsers = false;

  // ── Modal de crear usuario ──
  showCreateUserModal = false;
  savingUser = false;
  newUser: any = { name: '', lastName: '', username: '', password: '', email: '', phone: '', roles: [], shop: '' };
  newUserErrors: any[] = [];
  newUserFieldErrors: { [key: string]: string } = {};
  newUserTouched: { [key: string]: boolean } = {};
  showNewUserPassword = false;
  newUserSelectedRoleCode = '';

  // ── Modal de desactivación ──
  showDeactivateModal = false;
  deactivateUser: any = null;
  deactivationReason = '';
  deactivationNote = '';
  deactivationErrors: { [key: string]: string } = {};
  deactivationReasons = DEACTIVATION_REASONS;

  // ── Modal de editar contacto ──
  showEditContactModal = false;
  editContactUser: any = null;
  editContactEmail = '';
  editContactPhone = '';
  editContactErrors: { [key: string]: string } = {};
  savingContact = false;

  // ── Modal de cambiar contraseña ──
  showPasswordModal = false;
  passwordUser: any = null;
  newPassword = '';
  showPasswordValue = false;
  savingPassword = false;
  passwordError = '';

  // ── Modal de asignar tienda ──
  showShopModal = false;
  shopUser: any = null;
  selectedShopCode = '';
  savingShop = false;
  shops: any[] = [];

  // ── Modal de cambiar estatus ──
  savingStatus = false;

  // ── Modal de permisos de rol ──
  showPermissionsModal = false;
  selectedRoleForPermissions: any = null;
  rolePermissions: any = {};
  loadingPermissions = false;

  // ── Modal de crear rol ──
  showCreateRoleModal = false;
  savingRole = false;
  newRole = {
    code: '',
    name: '',
    description: '',
    department: '',
    level: 3
  };
  newRolePermissionsMatrix: any[] = [];
  newRoleExpandedModules: any = {};

  get departments(): string[] {
    const deps = new Set<string>();
    this.roles.forEach(r => { if (r.department) deps.add(r.department); });
    this.departmentsList.forEach(d => { if (d.name) deps.add(d.name); });
    return Array.from(deps).sort();
  }

  // ── Stats (dashboard) ──
  stats = {
    totalUsers: 0,
    usersWithRbac: 0,
    usersWithoutRbac: 0,
    totalRoles: 0,
    totalPermissions: 0,
    totalModules: 0,
    totalSubmodules: 0
  };

  // ── Permisos tab: Matriz de permisos ──
  selectedRoleForMatrix = '';
  permissionsMatrix: any[] = [];
  expandedModules: any = {};
  matrixDirty = false;
  savingMatrix = false;

  // ── Auditoría ──
  auditLogs: any[] = [];
  auditFilter = 'all';
  auditEntityFilter = 'all';
  auditSearch = '';
  auditDateFrom = '';
  auditDateTo = '';
  auditPage = 1;
  auditLimit = 15;
  auditTotal = 0;
  auditTotalPages = 1;
  loadingAudit = false;

  // ── Módulos y submódulos REALES del sistema ──
  // IDs deben coincidir EXACTAMENTE con los códigos de permisos en BD: module.submodule.action
  // Basado en PERMISSIONS array de rbac-seed.js
  systemModules: any[] = [
    { id: 'contactos', name: 'Contactos', icon: 'fas fa-address-book', submodules: [
      { id: 'busqueda', name: 'Búsqueda por Cédula', actions: ['read'] },
      { id: 'lista_contactos', name: 'Lista de Contactos', actions: ['read'] },
      { id: 'perfil', name: 'Perfil del Contacto', actions: ['read', 'update', 'delete'] },
      { id: 'onboarding', name: 'Onboarding', actions: ['read', 'editar_datos', 'editar_medios_pago', 'aprobar_rechazar', 'verificar_biometria', 'editar_selfie', 'editar_documento', 'editar_fecha', 'habilitar_biometria'] },
      { id: 'productos_comprados', name: 'Productos Comprados', actions: ['read'] },
      { id: 'vista_morosidad', name: 'Vista de morosidad', actions: ['ver_detalle', 'ver_detalle_completo'] },
      { id: 'gestion_sap', name: 'Gestión en SAP', actions: ['read'] },
      { id: 'gestion_odoo', name: 'Gestión en Odoo', actions: ['read', 'write'] },
      // { id: 'estatus', name: 'Estatus del Contacto', actions: ['read', 'update'] },
    ], sections: [
      { id: 'cupo', name: 'Cupos', icon: 'fas fa-search-dollar', moduleId: 'cupo', submodules: [
        { id: 'consultar_cupo', name: 'Consultar Cupo', actions: ['read', 'update'] },
        { id: 'estatus_cupo', name: 'Estatus de Cupo', actions: ['read', 'update'] },
        { id: 'cupo_habilitado', name: 'Cupo Habilitado', actions: ['read', 'update'] },
        { id: 'historial_cambios_cupo', name: 'Historial de Cambios', actions: ['read'] },
      ]},
      { id: 'planes', name: 'Planes', icon: 'fas fa-list-alt', moduleId: 'planes', submodules: [
        { id: 'consultar_planes', name: 'Consultar Planes', actions: ['read'] },
        { id: 'etiquetas', name: 'Etiquetas', actions: ['read', 'assign', 'delete'] },
      ]},
      { id: 'compras_financiadas', name: 'Compras Financiadas', icon: 'fas fa-shopping-cart', moduleId: 'compras_financiadas', submodules: [
        { id: 'filtrar_producto', name: 'Filtrar Producto', actions: ['read'] },
        { id: 'compras', name: 'Historial de Compras', actions: ['read'] },
        { id: 'pagares', name: 'Pagarés', actions: ['download'] },
        { id: 'polizas', name: 'Pólizas', actions: ['download'] },
        { id: 'anular_compra', name: 'Anular Compra', actions: ['cancel'] },
        { id: 'productos', name: 'Productos de la Compra', actions: ['read'] },
      ]},
      { id: 'gestion_cobro', name: 'Gestión de Cobro', icon: 'fas fa-hand-holding-usd', moduleId: 'gestion_cobro', submodules: [
        { id: 'consultar', name: 'Consultar Gestión de Cobro', actions: ['read'] },
      ]},
      { id: 'pagos', name: 'Pagos', icon: 'fas fa-money-check-alt', moduleId: 'pagos', submodules: [
        { id: 'pagos_registrados', name: 'Pagos Registrados', actions: ['read', 'update', 'approve', 'cancel', 'activate_review'] },
        { id: 'nuevo_pago', name: 'Nuevo Pago', actions: ['create'] },
        { id: 'otros_flujos', name: 'Otros Flujos de Pago', actions: ['read'] },
        { id: 'nota_credito', name: 'Notas de Crédito', actions: ['read', 'create'] },
      ]},
      { id: 'penalidades', name: 'Penalidades', icon: 'fas fa-exclamation-triangle', moduleId: 'pagos', submodules: [
        { id: 'penalidades', name: 'Consultar Penalidades', actions: ['read'] },
        { id: 'cancelar_penalidad', name: 'Cancelar Penalidad', actions: ['cancel'] },
      ]},
    ]},
    { id: 'ventas', name: 'Ventas', icon: 'fas fa-chart-bar', submodules: [
      { id: 'facturas', name: 'Compras Financiadas', actions: ['read'] },
    ], sections: [
      // "Simular crédito" (tarjeta del tablero de Ventas → /console/credit-save-v2).
      // Se declara como sección con moduleId 'compras_financiadas' para que el código
      // de permiso generado sea EXACTAMENTE 'compras_financiadas.credito.create'
      // (permiso ya existente en rbac-seed.js). Así queda gestionable de forma
      // explícita en la matriz, en vez de concederse solo por el comodín de Gerencia.
      { id: 'simular_credito', name: 'Simular Crédito', icon: 'fas fa-calculator', moduleId: 'compras_financiadas', submodules: [
        { id: 'credito', name: 'Crear Crédito', actions: ['create'] },
      ]},
    ]},
    { id: 'finanzas', name: 'Finanzas', icon: 'fas fa-chart-line', submodules: [
      { id: 'verificacion_pagos', name: 'Verificación de Pagos', actions: ['read', 'update', 'approve', 'cancel', 'activate_review'] },
    ]},
    { id: 'cobranza', name: 'Cobranza', icon: 'fas fa-file-invoice-dollar', submodules: [
      { id: 'domiciliaciones', name: 'Domiciliaciones Bancarias', actions: ['read', 'update', 'export', 'dashboard'] },
      { id: 'afiliaciones', name: 'Afiliaciones Bancarias', actions: ['read', 'update', 'export'] },
      { id: 'cuotas', name: 'Gestión de Cuotas', actions: ['read', 'update', 'export'] },
      { id: 'notificaciones', name: 'Notificaciones por Canal', actions: ['read', 'update'] },
      { id: 'banco_plaza', name: 'Banco Plaza - Débito Inmediato', actions: ['read'] },
      { id: 'bdv_consultas', name: 'Consultas BDV', actions: ['read'] },
      { id: 'pagos_caja', name: 'Pagos en Caja', actions: ['read'] },
      { id: 'distribucion_pagos', name: 'Distribución de Pagos', actions: ['read'] },
      { id: 'cartera_mora', name: 'Cartera y Mora', actions: ['read'] },
    ]},
    { id: 'credito', name: 'Crédito', icon: 'fas fa-credit-card', submodules: [
      { id: 'politicas_aprobacion', name: 'Políticas de Aprobación', actions: ['read'] },
      { id: 'estatus', name: 'Estatus de Aprobación', actions: ['read', 'update'] },
      { id: 'registro', name: 'Registro de Clientes', actions: ['read', 'update'] },
      { id: 'carga_masiva', name: 'Carga Masiva de Clientes', actions: ['read', 'create'] },
      { id: 'aprobacion_masiva', name: 'Aprobación Masiva de Clientes', actions: ['read', 'create'] },
      { id: 'aumento_cupos', name: 'Aumento Masivo de Cupos', actions: ['read', 'create'] },
      { id: 'aumento_cupos_rewards', name: 'Aumento Masivo de Cupos con Rewards', actions: ['read', 'create'] },
      { id: 'inactivacion_cupos', name: 'Inactivación Masiva de Cupos', actions: ['read', 'create'] },
      { id: 'historial_cupo', name: 'Historial de Cambios de Cupo', actions: ['read'] },
      { id: 'etiquetas', name: 'Carga Masiva de Etiquetas', actions: ['read', 'create'] },
      { id: 'carga_masiva_fees', name: 'Carga Masiva de Fees de Reactivación', actions: ['read', 'create'] },
    ]},
    { id: 'config', name: 'Parámetros del Sistema', icon: 'fas fa-cogs', submodules: [
      { id: 'roles_permisos', name: 'Roles y Permisos', actions: ['read', 'update'] },
      { id: 'integracion_sap', name: 'Integración SAP', actions: ['read'] },
      { id: 'integracion_odoo', name: 'Integración Odoo', actions: ['read', 'write'] },
      { id: 'seguridad', name: 'Seguridad Avanzada', actions: ['read', 'update', 'config'] },
      { id: 'tiendas', name: 'Gestión de Tiendas', actions: ['read', 'create', 'update'] },
      { id: 'metodos_pago', name: 'Métodos de Pago', actions: ['read', 'update', 'config'] },
      { id: 'productos', name: 'Productos', actions: ['read', 'create', 'config'] },
      { id: 'planes', name: 'Planes', actions: ['read', 'create', 'update', 'delete'] },
      { id: 'etiquetas', name: 'Etiquetas', actions: ['read', 'create', 'update', 'delete'] },
      { id: 'comisiones', name: 'Comisiones y Penalidades', actions: ['read', 'create', 'update', 'delete'] },
      { id: 'notificaciones', name: 'Notificaciones', actions: ['read', 'config'] },
      { id: 'tasas', name: 'Tasa de Cambio', actions: ['read', 'create', 'config'] },
      { id: 'pagos_async', name: 'Pagos Asíncronos', actions: ['read', 'config'] },
      { id: 'perfil_bancario', name: 'Perfil Bancario (switch de contratos)', actions: ['read', 'cambiar'] },
      { id: 'seguros', name: 'Seguros', actions: ['read'] },
      { id: 'app_movil', name: 'Aplicación Móvil', actions: ['read', 'create', 'update', 'delete', 'config'] },
      { id: 'clientes_app', name: 'Clientes con App', actions: ['read'] },
      { id: 'procesos_masivos', name: 'Procesos Masivos', actions: ['read'] },
      { id: 'preferencias', name: 'Preferencias', actions: ['read', 'config'] },
      { id: 'cartera_castigada', name: 'Cartera Castigada', actions: ['read'] },
      { id: 'domiciliacion_ct', name: 'Domiciliación con CrediTotal', actions: ['read', 'cambiar'] },
    ]},
    { id: 'reportes', name: 'Reportería', icon: 'fas fa-file-alt', submodules: [],
      sections: [
        { id: 'reportes_creditos', name: 'Reportes de Créditos', icon: 'fas fa-credit-card', moduleId: 'reportes', submodules: [
          { id: 'cartera_clientes', name: 'Reporte de Cartera de Clientes', actions: ['export', 'generate', 'stop'] },
          { id: 'cartera_credito', name: 'Reporte de Cartera de Crédito', actions: ['export', 'generate', 'stop'] },
          { id: 'clientes_domiciliados', name: 'Reporte de Clientes Domiciliados', actions: ['export', 'generate', 'stop'] },
          { id: 'cuotas', name: 'Reporte de Cuotas', actions: ['export', 'generate', 'stop'] },
          { id: 'distribucion_pagos', name: 'Reporte de Distribución de Pagos', actions: ['export', 'generate', 'stop'] },
          { id: 'biometria', name: 'Reporte de Biometría', actions: ['export', 'generate', 'stop'] },
          { id: 'pagos', name: 'Reporte de Pagos', actions: ['export', 'generate', 'stop'] },
          { id: 'estadistica_registros', name: 'Reporte de Estadística de Registros', actions: ['export', 'generate', 'stop'] },
          { id: 'pagos_penalidades', name: 'Reporte de Pagos a Penalidades', actions: ['export', 'generate', 'stop'] },
          { id: 'penalidades', name: 'Reporte de Penalidades', actions: ['export', 'generate', 'stop'] },
          { id: 'planes_clientes', name: 'Reporte de Planes por Clientes', actions: ['export', 'generate', 'stop'] },
          { id: 'bdv_transacciones', name: 'BDV BP Transacciones Encoladas', actions: ['export', 'generate', 'stop'] },
          { id: 'pagos_sap', name: 'Reporte Pagos SAP', actions: ['export', 'generate', 'stop'] },
          { id: 'giros_sap', name: 'Reporte Giros SAP', actions: ['export', 'generate', 'stop'] },
          { id: 'pagos_sap_nuevo', name: 'Reporte Pagos SAP Nuevo', actions: ['export', 'generate', 'stop'] },
          { id: 'pagos_sap_perdidas_incobrables', name: 'Reporte Pagos SAP Pérdidas Incobrables', actions: ['export', 'generate', 'stop'] },
          { id: 'giros_sap_nuevo', name: 'Reporte Giros SAP Nuevo', actions: ['export', 'generate', 'stop'] },
          { id: 'sap_business_partner', name: 'Reporte de SAP Business Partner', actions: ['export', 'generate', 'stop'] },
          { id: 'sap_partidas_abiertas', name: 'Reporte de SAP de Partidas Abiertas', actions: ['export', 'generate', 'stop'] },
          { id: 'sap_perdidas_incobrables', name: 'Reporte de SAP de Pérdidas Incobrables', actions: ['export', 'generate', 'stop'] },
          { id: 'fees_reactivacion', name: 'Reporte de Estado de Fees de Reactivación (C004)', actions: ['export', 'generate', 'stop'] },
          { id: 'cartera_360', name: 'Reporte 360 Salud de Cartera (Ajuste 19)', actions: ['export', 'generate', 'stop'] },
        ]},
        { id: 'reportes_seguros', name: 'Reportes de Seguros', icon: 'fas fa-shield-alt', moduleId: 'reportes', submodules: [
          { id: 'seguros', name: 'Reporte de Seguros', actions: ['export', 'generate', 'stop'] },
          { id: 'pagos_seguros', name: 'Reporte de Pagos de Seguros Mercantil', actions: ['export', 'generate', 'stop'] },
          { id: 'operativo_seguros', name: 'Reporte Operativo Seguros Mercantil', actions: ['export', 'generate', 'stop'] },
          { id: 'operativo_seguros_caracas', name: 'Reporte Operativo Seguros Caracas', actions: ['export', 'generate', 'stop'] },
          { id: 'cartera_seguros', name: 'Cartera de Seguros', actions: ['export', 'generate', 'stop'] },
          { id: 'pagos_seguro_kredit', name: 'Reporte de Pagos Seguro Kredit', actions: ['export', 'generate', 'stop'] },
          { id: 'cuotas_seguros', name: 'Reporte de Cuotas Seguros', actions: ['export', 'generate', 'stop'] },
          { id: 'sap_mercantil_insurer', name: 'Reporte SAP Pagos - Seguros Mercantil', actions: ['export', 'generate', 'stop'] },
          { id: 'sap_caracas_insurer', name: 'Reporte SAP Pagos - Seguros Caracas', actions: ['export', 'generate', 'stop'] },
        ]},
      ]
    },
    { id: 'productos_compra', name: 'Productos de Compra', icon: 'fas fa-box-open', submodules: [
      { id: 'catalogo', name: 'Catálogo', actions: ['read', 'create', 'update', 'delete', 'export'] },
    ]},
    // El módulo `seguros` faltaba entero. Sus permisos existen en la BD desde
    // junio, pero este árbol sólo declaraba `config.seguros.read` —dentro de
    // Parámetros del Sistema—, así que `seguros.reporte_pagos.*` no se podía
    // asignar desde aquí: no se veía. De ahí que el único permiso de seguros
    // repartido sea el de lectura, y que acabara autorizando envíos reales a la
    // aseguradora.
    { id: 'seguros', name: 'Seguros', icon: 'fas fa-shield-alt', submodules: [
      { id: 'reporte_pagos', name: 'Reporte de Pagos a la Aseguradora', actions: ['read', 'manage'] },
      // Las tres acciones del reporte por CUOTA, separadas porque su riesgo no
      // se parece: la traza no cambia nada, preguntar reescribe el espejo de la
      // póliza, y reportar notifica un pago a Mercantil y no se deshace.
      { id: 'cuotas', name: 'Reporte de Cuotas a la Aseguradora', actions: ['traza', 'consultar_sm', 'reportar'] },
    ]},
  ];

  // ── Catálogo de acciones estándar ──
  actionLabels: any = {
    'read': { name: 'Consultar', icon: 'fas fa-eye', color: 'blue' },
    'search': { name: 'Buscar', icon: 'fas fa-search', color: 'blue' },
    'create': { name: 'Crear', icon: 'fas fa-plus', color: 'green' },
    'update': { name: 'Editar', icon: 'fas fa-edit', color: 'yellow' },
    'delete': { name: 'Eliminar', icon: 'fas fa-trash', color: 'red' },
    'approve': { name: 'Aprobar', icon: 'fas fa-check-circle', color: 'green' },
    'reject': { name: 'Rechazar', icon: 'fas fa-times-circle', color: 'red' },
    'cancel': { name: 'Anular', icon: 'fas fa-ban', color: 'red' },
    'assign': { name: 'Asignar', icon: 'fas fa-link', color: 'teal' },
    'download': { name: 'Descargar', icon: 'fas fa-download', color: 'purple' },
    'upload': { name: 'Cargar', icon: 'fas fa-upload', color: 'purple' },
    'activate': { name: 'Activar', icon: 'fas fa-toggle-on', color: 'green' },
    'deactivate': { name: 'Desactivar', icon: 'fas fa-toggle-off', color: 'orange' },
    'activate_review': { name: 'Activar Revisión', icon: 'fas fa-clipboard-check', color: 'yellow' },
    'config': { name: 'Configurar', icon: 'fas fa-cog', color: 'gray' },
    'write': { name: 'Administrar', icon: 'fas fa-cogs', color: 'orange' },
    'manage': { name: 'Gestionar', icon: 'fas fa-cogs', color: 'orange' },
    'cambiar': { name: 'Cambiar', icon: 'fas fa-exchange-alt', color: 'red' },
    // Seguros — reporte por cuota. Sin etiqueta se pintaría el código crudo.
    'traza': { name: 'Ver Traza', icon: 'fas fa-history', color: 'blue' },
    'consultar_sm': { name: 'Preguntar a la Aseguradora', icon: 'fas fa-question-circle', color: 'teal' },
    'reportar': { name: 'Reportar a la Aseguradora', icon: 'fas fa-paper-plane', color: 'red' },
    'execute': { name: 'Ejecutar', icon: 'fas fa-play', color: 'blue' },
    'generate': { name: 'Generar', icon: 'fas fa-file-export', color: 'green' },
    'export': { name: 'Exportar', icon: 'fas fa-file-download', color: 'purple' },
    'stop': { name: 'Detener', icon: 'fas fa-stop-circle', color: 'red' },
    'history': { name: 'Historial', icon: 'fas fa-history', color: 'blue' },
    'editar_datos': { name: 'Editar Datos', icon: 'fas fa-id-card', color: 'yellow' },
    'editar_medios_pago': { name: 'Editar Medios de Pago', icon: 'fas fa-credit-card', color: 'yellow' },
    'aprobar_rechazar': { name: 'Aprobar/Rechazar', icon: 'fas fa-gavel', color: 'purple' },
    'verificar_biometria': { name: 'Verificar/No Verificar Biometría', icon: 'fas fa-fingerprint', color: 'purple' },
    'editar_selfie': { name: 'Editar Selfie', icon: 'fas fa-camera', color: 'yellow' },
    'editar_documento': { name: 'Editar Documento', icon: 'fas fa-id-badge', color: 'yellow' },
    'editar_fecha': { name: 'Editar Fecha', icon: 'fas fa-calendar-alt', color: 'yellow' },
    'habilitar_biometria': { name: 'Habilitar Biometría', icon: 'fas fa-fingerprint', color: 'green' },
    'dashboard': { name: 'Dashboard', icon: 'fas fa-cog', color: 'gray' },
    'ver_detalle': { name: 'Ver detalle', icon: 'fas fa-eye', color: 'blue' },
    'ver_detalle_completo': { name: 'Ver detalle completo', icon: 'fas fa-external-link-alt', color: 'indigo' }
  };

  // Cache local de permisos por rol (se llena desde BD, ya no está hardcodeado)
  rolePermissionsMap: any = {};

  constructor(
    private rbacService: RbacAdminService,
    private _user: UserService,
    public _auth: AuthService
  ) { }

  ngOnInit(): void {
    this.loadData();
    this.setupRealtimeSearch();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  setupRealtimeSearch() {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe((term: string) => {
      // Solo buscar si tiene 2+ caracteres o está vacío (reset)
      if (term.length === 0 || term.length >= 2) {
        this.usersPage = 1;
        this.loadUsers();
      }
    });
  }

  onSearchInput() {
    this.searchSubject.next(this.searchTerm);
  }

  // ══════════════════════════════════════════
  //  DATA LOADING
  // ══════════════════════════════════════════

  async loadData() {
    this.loading = true;
    try {
      await Promise.all([this.loadRoles(), this.loadUsers(), this.loadShops(), this.loadDepartments()]);
      this.calculateStats();
    } catch (error) {
      console.error('Error loading RBAC data:', error);
    }
    this.loading = false;
  }

  async loadRoles() {
    try {
      const resp = await this.rbacService.getRoles();
      this.roles = resp?.resp || [];
    } catch (error) {
      console.error('Error loading roles:', error);
      this.roles = [];
    }
  }

  async loadUsers() {
    this.loadingUsers = true;
    try {
      const resp = await this.rbacService.getUsers({
        page: this.usersPage,
        limit: this.usersLimit,
        search: this.searchTerm,
        status: this.statusFilter === 'active' ? 'true' : 'false',
        role: this.filterRole || undefined,
        department: this.filterDepartment || undefined,
        level: this.filterLevel || undefined
      });
      this.users = resp?.resp?.rows || [];
      this.usersTotal = resp?.resp?.total || 0;
      this.usersTotalPages = Math.ceil(this.usersTotal / this.usersLimit) || 1;
    } catch (error) {
      console.error('Error loading users:', error);
    }
    this.loadingUsers = false;
  }

  // Alterna el ordenamiento al clickear la cabecera: null → asc → desc → null
  sortUsersBy(column: 'username' | 'name' | 'email' | 'role') {
    if (this.usersSortColumn !== column) {
      this.usersSortColumn = column;
      this.usersSortDirection = 'asc';
    } else if (this.usersSortDirection === 'asc') {
      this.usersSortDirection = 'desc';
    } else {
      this.usersSortColumn = null;
    }
  }

  // Lista ordenada sobre la página visible (sort client-side)
  get sortedUsers(): any[] {
    if (!this.usersSortColumn) return this.users;
    const dir = this.usersSortDirection === 'asc' ? 1 : -1;
    const getKey = (u: any): string => {
      switch (this.usersSortColumn) {
        case 'username': return (u?.username || '').toString().toLowerCase();
        case 'name':     return `${u?.name || ''} ${u?.lastName || ''}`.trim().toLowerCase();
        case 'email':    return (u?.email || '').toString().toLowerCase();
        case 'role':     return (u?.rbacRoles?.[0]?.name || u?.rbacRole?.name || '').toString().toLowerCase();
        default:         return '';
      }
    };
    return [...this.users].sort((a, b) => {
      const ka = getKey(a);
      const kb = getKey(b);
      // Filas sin valor quedan al final (sin importar dirección)
      if (!ka && kb) return 1;
      if (ka && !kb) return -1;
      if (ka < kb) return -1 * dir;
      if (ka > kb) return  1 * dir;
      return 0;
    });
  }

  async loadShops() {
    try {
      const resp = await this._user.getshops({});
      this.shops = resp?.res || [];
    } catch (error) {
      console.error('Error loading shops:', error);
      this.shops = [];
    }
  }

  async loadAuditLogs() {
    this.loadingAudit = true;
    try {
      const resp = await this.rbacService.getAuditLogs({
        page: this.auditPage,
        limit: this.auditLimit,
        action: this.auditFilter,
        entity: this.auditEntityFilter,
        search: this.auditSearch,
        dateFrom: this.auditDateFrom,
        dateTo: this.auditDateTo
      });
      this.auditLogs = resp?.resp?.rows || [];
      this.auditTotal = resp?.resp?.total || 0;
      this.auditTotalPages = Math.ceil(this.auditTotal / this.auditLimit) || 1;
    } catch (error) {
      console.error('Error loading audit logs:', error);
      this.auditLogs = [];
    }
    this.loadingAudit = false;
  }

  onAuditFilterChange() {
    this.auditPage = 1;
    this.loadAuditLogs();
  }

  goToAuditPage(page: number) {
    if (page < 1 || page > this.auditTotalPages) return;
    this.auditPage = page;
    this.loadAuditLogs();
  }

  getAuditIcon(action: string): string {
    const icons: any = {
      'ASSIGN': 'fas fa-user-shield',
      'REVOKE': 'fas fa-user-minus',
      'CREATE': 'fas fa-plus-circle',
      'UPDATE': 'fas fa-edit',
      'DELETE': 'fas fa-trash-alt',
      'ACTIVATE': 'fas fa-toggle-on',
      'DEACTIVATE': 'fas fa-ban',
      'LOGIN': 'fas fa-sign-in-alt',
      'LOGOUT': 'fas fa-sign-out-alt',
      'LOGIN_FAILED': 'fas fa-exclamation-triangle',
      'ACCESS_DENIED': 'fas fa-lock'
    };
    return icons[action] || 'fas fa-circle';
  }

  getAuditColor(action: string): string {
    const colors: any = {
      'ASSIGN': 'text-green-600',
      'REVOKE': 'text-red-600',
      'CREATE': 'text-blue-600',
      'UPDATE': 'text-orange-600',
      'DELETE': 'text-red-700',
      'ACTIVATE': 'text-green-600',
      'DEACTIVATE': 'text-red-600',
      'LOGIN': 'text-blue-500',
      'LOGOUT': 'text-gray-500',
      'LOGIN_FAILED': 'text-yellow-600',
      'ACCESS_DENIED': 'text-red-500'
    };
    return colors[action] || 'text-gray-600';
  }

  getAuditActionLabel(action: string): string {
    const labels: any = {
      'ASSIGN': 'Asignación',
      'REVOKE': 'Remoción',
      'CREATE': 'Creación',
      'UPDATE': 'Actualización',
      'DELETE': 'Eliminación',
      'ACTIVATE': 'Activación',
      'DEACTIVATE': 'Desactivación',
      'LOGIN': 'Inicio de sesión',
      'LOGOUT': 'Cierre de sesión',
      'LOGIN_FAILED': 'Login fallido',
      'ACCESS_DENIED': 'Acceso denegado'
    };
    return labels[action] || action;
  }

  getAuditEntityLabel(entity: string): string {
    const labels: any = {
      'USER': 'Usuario',
      'ROLE': 'Rol',
      'PERMISSION': 'Permiso',
      'ROLE_PERMISSION': 'Permisos de rol',
      'USER_ROLE_ASSIGNMENT': 'Asignación de rol',
      'FEATURE_FLAG': 'Feature flag',
      'AUTH': 'Autenticación',
      'SYSTEM': 'Sistema'
    };
    return labels[entity] || entity;
  }

  getAuditStatusLabel(status: string): string {
    const labels: any = {
      'SUCCESS': 'Exitoso',
      'FAILURE': 'Fallido',
      'WARNING': 'Advertencia'
    };
    return labels[status] || status;
  }

  getAuditDetail(log: any): string {
    const target = log.metadata?.targetUsername || log.changesAfter?.username || '';
    const roleInfo = log.changesAfter?.roleName || log.changesAfter?.roleCode || '';
    const field = log.metadata?.field || '';

    switch (log.action) {
      case 'ASSIGN': return `Rol ${roleInfo} asignado a ${target}`;
      case 'REVOKE': return `Rol ${log.changesAfter?.roleCode || 'todos'} removido de ${target}`;
      case 'CREATE':
        if (log.entity === 'USER') return `Usuario ${target} creado`;
        if (log.entity === 'ROLE') return `Rol ${roleInfo} creado`;
        return `${this.getAuditEntityLabel(log.entity)} creado`;
      case 'UPDATE':
        if (field === 'password') return `Contraseña cambiada para ${target}`;
        if (field === 'shop') return `Tienda asignada a ${target}`;
        if (field === 'contact') return `Contacto actualizado para ${target}`;
        if (log.entity === 'ROLE_PERMISSION') return `Permisos actualizados para rol ${roleInfo}`;
        if (log.entity === 'ROLE') return `Rol ${roleInfo} actualizado`;
        return `${this.getAuditEntityLabel(log.entity)} actualizado`;
      case 'DELETE': return `Rol ${log.changesBefore?.roleName || ''} eliminado`;
      case 'ACTIVATE': return `Usuario ${target} activado`;
      case 'DEACTIVATE':
        const note = log.changesAfter?.note ? ` — "${log.changesAfter.note}"` : '';
        return `Usuario ${target} desactivado${note}`;
      case 'ACCESS_DENIED': return `Acceso denegado a ${target || this.getAuditEntityLabel(log.entity)}`;
      default: return `${this.getAuditActionLabel(log.action)} en ${this.getAuditEntityLabel(log.entity)}`;
    }
  }

  calculateStats() {
    this.stats.totalUsers = this.usersTotal;
    this.stats.usersWithRbac = this.users.filter(u => u.rbacRoles?.length > 0 || u.rbacRole).length;
    this.stats.usersWithoutRbac = this.users.filter(u => !u.rbacRoles?.length && !u.rbacRole).length;
    this.stats.totalRoles = this.roles.length;
    this.stats.totalModules = this.systemModules.length;
    let totalSub = 0;
    this.systemModules.forEach((m: any) => {
      totalSub += m.submodules?.length || 0;
      (m.sections || []).forEach((sec: any) => totalSub += sec.submodules?.length || 0);
    });
    this.stats.totalSubmodules = totalSub;
    this.stats.totalPermissions = totalSub;
  }

  // ══════════════════════════════════════════
  //  TAB: USUARIOS
  // ══════════════════════════════════════════

  onSearch() {
    this.usersPage = 1;
    this.loadUsers();
  }

  clearSearch() {
    this.searchTerm = '';
    this.usersPage = 1;
    this.loadUsers();
  }

  switchStatus(status: 'active' | 'inactive') {
    this.statusFilter = status;
    this.usersPage = 1;
    this.loadUsers();
  }

  onFilterChange() {
    this.usersPage = 1;
    this.loadUsers();
  }

  // ══════════════════════════════════════════
  //  PERMISOS POR USUARIO
  // ══════════════════════════════════════════

  async loadIndividualPermissions() {
    this.loadingIndividual = true;
    try {
      const resp = await this.rbacService.getIndividualPermissions();
      this.individualCatalog = resp?.catalog || [];
      this.individualGrants = new Set((resp?.grants || []).map((g: any) => `${g.userId}|${g.code}`));
    } catch (error) {
      console.error('Error loading individual permissions:', error);
      alertifyjs.error('No se pudieron cargar los permisos especiales');
    }
    this.loadingIndividual = false;
  }

  hasIndividual(user: any, code: string): boolean {
    return this.individualGrants.has(`${user._id}|${code}`);
  }

  async toggleIndividual(user: any, perm: { code: string; name: string }) {
    const key = `${user._id}|${perm.code}`;
    if (this.savingIndividual) return;
    const activar = !this.individualGrants.has(key);
    this.savingIndividual = key;
    try {
      await this.rbacService.setUserIndividualPermission(user._id, perm.code, activar);
      if (activar) this.individualGrants.add(key); else this.individualGrants.delete(key);
      alertifyjs.success(`${perm.name}: ${activar ? 'activado' : 'desactivado'} para ${user.username}`);
      // Refresca "Permisos Efectivos" del modal abierto
      if (this.showUserDetailModal && this.userDetailTarget?._id === user._id) {
        const resp = await this.rbacService.getUserPermissions(user._id);
        this.userDetailData = resp?.resp || this.userDetailData;
      }
    } catch (error: any) {
      alertifyjs.error(error?.error?.msg || 'No se pudo actualizar el permiso');
    }
    this.savingIndividual = null;
  }

  goToPage(page: number) {
    if (page < 1 || page > this.usersTotalPages) return;
    this.usersPage = page;
    this.loadUsers();
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, this.usersPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.usersTotalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  // Filters are now server-side — no client-side filtering needed

  get uniqueDepartments(): string[] {
    const deps = new Set<string>();
    this.roles.forEach(r => { if (r.department) deps.add(r.department); });
    return Array.from(deps).sort();
  }

  clearFilters() {
    this.filterRole = '';
    this.filterDepartment = '';
    this.filterLevel = '';
  }

  getShopName(shopCode: string): string {
    const shop = this.shops.find((s: any) => s.code === shopCode);
    return shop?.name || '';
  }

  // ── Dropdown Acciones (posicionamiento fijo) ──
  activeMenuUser: any = null;
  menuPosition = { top: 0, left: 0 };

  toggleActionsMenu(user: any, event: MouseEvent) {
    if (this.activeMenuUser === user) {
      this.activeMenuUser = null;
      return;
    }
    const btn = event.target as HTMLElement;
    const rect = btn.closest('button')!.getBoundingClientRect();
    const menuHeight = 250; // altura aproximada del menú
    const spaceBelow = window.innerHeight - rect.bottom;

    this.menuPosition = {
      top: spaceBelow < menuHeight ? rect.top - menuHeight : rect.bottom + 4,
      left: rect.right - 208 // 208 = w-52 (13rem)
    };
    this.activeMenuUser = user;
  }

  closeAllMenus() {
    this.activeMenuUser = null;
    this.users.forEach((u: any) => u._showActions = false);
  }

  // ── Crear Usuario ──

  openCreateUserModal() {
    this.newUser = { name: '', lastName: '', username: '', password: '', email: '', phone: '', roles: [], shop: '' };
    this.newUserErrors = [];
    this.newUserFieldErrors = {};
    this.newUserTouched = {};
    this.showNewUserPassword = false;
    this.newUserSelectedRoleCode = '';
    this.showCreateUserModal = true;
  }

  closeCreateUserModal() {
    this.showCreateUserModal = false;
    this.newUserFieldErrors = {};
    this.newUserTouched = {};
  }

  // Validacion en vivo para nuevo usuario
  onNewUserFieldBlur(field: string) {
    this.newUserTouched[field] = true;
    this.validateNewUserField(field);
  }

  validateNewUserField(field: string) {
    const value = (this.newUser[field] || '').toString().trim();
    const errors = this.newUserFieldErrors;
    if (field === 'name') {
      errors['name'] = !value ? VALIDATION_MESSAGES.nameRequired : '';
    } else if (field === 'lastName') {
      errors['lastName'] = !value ? VALIDATION_MESSAGES.lastNameRequired : '';
    } else if (field === 'username') {
      if (!value) errors['username'] = VALIDATION_MESSAGES.usernameRequired;
      else if (!isValidUsername(value)) errors['username'] = VALIDATION_MESSAGES.usernameInvalid;
      else errors['username'] = '';
    } else if (field === 'password') {
      if (!value) errors['password'] = VALIDATION_MESSAGES.passwordRequired;
      else if (!isValidPassword(value)) errors['password'] = VALIDATION_MESSAGES.passwordInvalid;
      else errors['password'] = '';
    } else if (field === 'email') {
      errors['email'] = value && !isValidEmail(value) ? VALIDATION_MESSAGES.emailInvalid : '';
    } else if (field === 'phone') {
      errors['phone'] = value && !isValidPhoneVE(value) ? VALIDATION_MESSAGES.phoneInvalid : '';
    }
  }

  onNewUserPhoneInput(event: any) {
    const sanitized = sanitizePhone(event.target.value);
    this.newUser.phone = sanitized;
    // Forzar actualizacion del DOM si el input trae caracteres no validos
    if (event.target.value !== sanitized) {
      event.target.value = sanitized;
    }
    if (this.newUserTouched['phone']) this.validateNewUserField('phone');
  }

  onPhoneKeyPress(event: KeyboardEvent): boolean {
    // Permitir solo numeros, backspace, tab, delete y flechas
    const key = event.key;
    if (key.length === 1 && !/[0-9]/.test(key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  isNewUserFormValid(): boolean {
    const u = this.newUser;
    if (!u.name || !u.name.trim()) return false;
    if (!u.lastName || !u.lastName.trim()) return false;
    if (!isValidUsername((u.username || '').trim())) return false;
    if (!isValidPassword(u.password || '')) return false;
    if (u.email && u.email.trim() && !isValidEmail(u.email.trim())) return false;
    if (u.phone && u.phone.trim() && !isValidPhoneVE(u.phone.trim())) return false;
    return true;
  }

  validateAllNewUserFields() {
    ['name', 'lastName', 'username', 'password', 'email', 'phone'].forEach(f => {
      this.newUserTouched[f] = true;
      this.validateNewUserField(f);
    });
  }

  addRoleToNewUser() {
    if (!this.newUserSelectedRoleCode) return;
    if (this.newUser.roles.some((r: any) => r.code === this.newUserSelectedRoleCode)) return;
    const role = this.roles.find((r: any) => r.code === this.newUserSelectedRoleCode);
    if (role) {
      this.newUser.roles.push({ code: role.code, name: role.name, department: role.department });
    }
    this.newUserSelectedRoleCode = '';
  }

  removeRoleFromNewUser(roleCode: string) {
    this.newUser.roles = this.newUser.roles.filter((r: any) => r.code !== roleCode);
  }

  getAvailableRolesForNewUser(): any[] {
    const assignedCodes = this.newUser.roles.map((r: any) => r.code);
    return this.roles.filter((r: any) => !assignedCodes.includes(r.code));
  }

  async createUser() {
    this.validateAllNewUserFields();
    if (!this.isNewUserFormValid()) {
      alertifyjs.error('Corrige los campos marcados en rojo antes de continuar');
      return;
    }
    this.savingUser = true;
    this.newUserErrors = [];
    try {
      const body: any = {
        name: this.newUser.name,
        lastName: this.newUser.lastName,
        type: 'PERSON',
        username: this.newUser.username,
        password: this.newUser.password,
        audience: 'NATURAL',
        verificated_contact: false,
        role: 'USER_ROLE',
        email: this.newUser.email || null,
        phone: this.newUser.phone || null,
        shop: this.newUser.shop || null,
        roles: this.newUser.roles.map((r: any) => r.code)
      };
      await this._user.createUser(body);
      alertifyjs.success('Usuario creado exitosamente');
      this.closeCreateUserModal();
      await this.loadUsers();
      this.calculateStats();
    } catch (error: any) {
      if (error?.error?.msg?.includes('ya está en uso')) {
        alertifyjs.error('Ese nombre de usuario ya está en uso');
      } else if (error?.error?.errors && Array.isArray(error.error.errors)) {
        // Mapear errores por campo a newUserFieldErrors
        error.error.errors.forEach((e: any) => {
          if (e.field) {
            this.newUserFieldErrors[e.field] = e.msg;
            this.newUserTouched[e.field] = true;
          }
        });
        this.newUserErrors = error.error.errors;
        alertifyjs.error(error.error.msg || 'Corrige los campos marcados');
      } else {
        if (error?.error?.msg) this.newUserErrors = [{ msg: error.error.msg }];
        alertifyjs.error('Error al crear el usuario');
      }
    }
    this.savingUser = false;
  }

  // ── Cambiar Contraseña ──

  openPasswordModal(user: any) {
    this.passwordUser = user;
    this.newPassword = '';
    this.showPasswordValue = false;
    this.passwordError = '';
    this.showPasswordModal = true;
  }

  closePasswordModal() {
    this.showPasswordModal = false;
    this.passwordUser = null;
    this.newPassword = '';
    this.passwordError = '';
  }

  onPasswordInput() {
    if (!this.newPassword) {
      this.passwordError = VALIDATION_MESSAGES.passwordRequired;
    } else if (!isValidPassword(this.newPassword)) {
      this.passwordError = VALIDATION_MESSAGES.passwordInvalid;
    } else {
      this.passwordError = '';
    }
  }

  isPasswordValid(): boolean {
    return isValidPassword(this.newPassword);
  }

  async changePassword() {
    if (!this.passwordUser) return;
    this.onPasswordInput();
    if (!this.isPasswordValid()) {
      alertifyjs.error(VALIDATION_MESSAGES.passwordInvalid);
      return;
    }
    this.savingPassword = true;
    try {
      await this._user.pass_user({
        code_user: this.passwordUser.code,
        password: this.newPassword
      });
      alertifyjs.success('Contraseña cambiada exitosamente');
      this.closePasswordModal();
    } catch (error: any) {
      const msg = error?.error?.msg || 'Error al cambiar la contraseña';
      this.passwordError = msg;
      alertifyjs.error(msg);
    }
    this.savingPassword = false;
  }

  // ── Asignar Tienda ──

  openShopModal(user: any) {
    this.shopUser = user;
    this.selectedShopCode = user.shop || '';
    this.showShopModal = true;
  }

  closeShopModal() {
    this.showShopModal = false;
    this.shopUser = null;
    this.selectedShopCode = '';
  }

  async changeShop() {
    if (!this.shopUser) return;
    this.savingShop = true;
    try {
      await this._user.shop_user({
        code_user: this.shopUser.code,
        code_shop: this.selectedShopCode
      });
      alertifyjs.success('Tienda asignada exitosamente');
      this.closeShopModal();
      await this.loadUsers();
    } catch (error: any) {
      alertifyjs.error('Error al asignar tienda');
    }
    this.savingShop = false;
  }

  // ── Toggle Estatus ──

  async toggleUserStatus(user: any) {
    if (user.status) {
      // Si está activo, abrir modal de desactivación para pedir motivo
      this.deactivateUser = user;
      this.deactivationReason = '';
      this.deactivationNote = '';
      this.deactivationErrors = {};
      this.showDeactivateModal = true;
    } else {
      // Si está inactivo, activar directamente
      this.savingStatus = true;
      try {
        await this._user.status_user({
          code_user: user.code,
          status: true
        });
        alertifyjs.success('Usuario activado');
        await this.loadUsers();
      } catch (error: any) {
        alertifyjs.error('Error al activar usuario');
      }
      this.savingStatus = false;
    }
  }

  closeDeactivateModal() {
    this.showDeactivateModal = false;
    this.deactivateUser = null;
    this.deactivationReason = '';
    this.deactivationNote = '';
    this.deactivationErrors = {};
  }

  getDeactivationReasonLabel(value: string): string {
    const reason = this.deactivationReasons.find(r => r.value === value);
    return reason ? reason.label : value;
  }

  onDeactivationReasonChange() {
    if (this.deactivationReason) this.deactivationErrors['reason'] = '';
    // Si no es OTRO limpiamos nota para evitar confusion
    if (this.deactivationReason !== 'OTRO') {
      this.deactivationNote = '';
      this.deactivationErrors['note'] = '';
    }
  }

  validateDeactivation(): boolean {
    this.deactivationErrors = {};
    if (!this.deactivationReason) {
      this.deactivationErrors['reason'] = VALIDATION_MESSAGES.deactivationReasonRequired;
    }
    if (this.deactivationReason === 'OTRO' && !this.deactivationNote.trim()) {
      this.deactivationErrors['note'] = VALIDATION_MESSAGES.deactivationNoteRequired;
    }
    return Object.keys(this.deactivationErrors).length === 0;
  }

  async confirmDeactivation() {
    if (!this.deactivateUser) return;
    if (!this.validateDeactivation()) return;
    this.savingStatus = true;
    try {
      await this._user.status_user({
        code_user: this.deactivateUser.code,
        status: false,
        reason: this.deactivationReason,
        note: this.deactivationReason === 'OTRO' ? this.deactivationNote : null
      });
      alertifyjs.success('Usuario desactivado');
      this.closeDeactivateModal();
      await this.loadUsers();
    } catch (error: any) {
      alertifyjs.error(error?.error?.msg || 'Error al desactivar usuario');
    }
    this.savingStatus = false;
  }

  // ── Editar Contacto ──

  openEditContactModal(user: any) {
    this.editContactUser = user;
    this.editContactEmail = user.email || '';
    this.editContactPhone = user.phone || '';
    this.editContactErrors = {};
    this.showEditContactModal = true;
  }

  closeEditContactModal() {
    this.showEditContactModal = false;
    this.editContactUser = null;
    this.editContactEmail = '';
    this.editContactPhone = '';
    this.editContactErrors = {};
  }

  onEditContactEmailBlur() {
    if (this.editContactEmail && !isValidEmail(this.editContactEmail.trim())) {
      this.editContactErrors['email'] = VALIDATION_MESSAGES.emailInvalid;
    } else {
      this.editContactErrors['email'] = '';
    }
  }

  onEditContactPhoneInput(event: any) {
    const sanitized = sanitizePhone(event.target.value);
    this.editContactPhone = sanitized;
    // Forzar sincronizacion con el DOM cuando el usuario pega texto con letras
    if (event.target.value !== sanitized) {
      event.target.value = sanitized;
    }
    if (this.editContactPhone && !isValidPhoneVE(this.editContactPhone)) {
      this.editContactErrors['phone'] = VALIDATION_MESSAGES.phoneInvalid;
    } else {
      this.editContactErrors['phone'] = '';
    }
  }

  isEditContactValid(): boolean {
    if (this.editContactEmail && !isValidEmail(this.editContactEmail.trim())) return false;
    if (this.editContactPhone && !isValidPhoneVE(this.editContactPhone)) return false;
    return true;
  }

  async saveContactInfo() {
    if (!this.editContactUser) return;
    this.onEditContactEmailBlur();
    if (!this.isEditContactValid()) {
      alertifyjs.error('Corrige los campos marcados en rojo');
      return;
    }
    this.savingContact = true;
    try {
      await this._user.contact_user({
        code_user: this.editContactUser.code,
        email: this.editContactEmail,
        phone: this.editContactPhone
      });
      alertifyjs.success('Datos de contacto actualizados');
      this.closeEditContactModal();
      await this.loadUsers();
    } catch (error: any) {
      alertifyjs.error(error?.error?.msg || 'Error al actualizar contacto');
    }
    this.savingContact = false;
  }

  openAssignModal(user: any) {
    this.selectedUser = user;
    this.selectedRoleCode = '';
    // Copiar roles actuales al staging
    this.stagedRoles = (user.rbacRoles || []).map((r: any) => ({ ...r }));
    this.originalRoleCodes = (user.rbacRoles || []).map((r: any) => r.code);
    this.showAssignModal = true;
  }

  closeAssignModal() {
    this.showAssignModal = false;
    this.selectedUser = null;
    this.selectedRoleCode = '';
    this.stagedRoles = [];
    this.originalRoleCodes = [];
  }

  addRoleToStaging() {
    if (!this.selectedRoleCode) return;
    const alreadyStaged = this.stagedRoles.some((r: any) => r.code === this.selectedRoleCode);
    if (alreadyStaged) return;
    const role = this.roles.find((r: any) => r.code === this.selectedRoleCode);
    if (role) {
      this.stagedRoles.push({ code: role.code, name: role.name, department: role.department, level: role.level });
      this.stagedRoles.sort((a: any, b: any) => a.name.localeCompare(b.name));
    }
    this.selectedRoleCode = '';
  }

  removeStagedRole(roleCode: string) {
    this.stagedRoles = this.stagedRoles.filter((r: any) => r.code !== roleCode);
  }

  clearStagedRoles() {
    this.stagedRoles = [];
  }

  getAvailableRolesForUser(): any[] {
    const stagedCodes = this.stagedRoles.map((r: any) => r.code);
    return this.roles.filter((r: any) => !stagedCodes.includes(r.code));
  }

  get hasRoleChanges(): boolean {
    const stagedCodes = this.stagedRoles.map((r: any) => r.code).sort();
    const origCodes = [...this.originalRoleCodes].sort();
    if (stagedCodes.length !== origCodes.length) return true;
    return stagedCodes.some((code: string, i: number) => code !== origCodes[i]);
  }

  async applyRoleChanges() {
    if (!this.selectedUser || !this.hasRoleChanges) return;
    this.saving = true;
    try {
      const stagedCodes = this.stagedRoles.map((r: any) => r.code);
      const origCodes = this.originalRoleCodes;

      // Roles a agregar (están en staged pero no en original)
      const toAdd = stagedCodes.filter((c: string) => !origCodes.includes(c));
      // Roles a remover (están en original pero no en staged)
      const toRemove = origCodes.filter((c: string) => !stagedCodes.includes(c));

      // Remover primero
      for (const code of toRemove) {
        await this.rbacService.removeRole(this.selectedUser._id, code);
      }
      // Agregar
      for (const code of toAdd) {
        await this.rbacService.assignRole(this.selectedUser._id, code);
      }

      alertifyjs.success(`Roles actualizados para ${this.selectedUser.username}`);
      this.closeAssignModal();
      await this.loadUsers();
      this.calculateStats();
    } catch (error: any) {
      alertifyjs.error(error?.error?.msg || 'Error al actualizar roles');
    }
    this.saving = false;
  }

  // ── Detalle de Usuario (roles + permisos, solo lectura) ──

  async openUserDetailModal(user: any) {
    this.userDetailData = null;
    this.userDetailTarget = user;
    this.showUserDetailModal = true;
    this.loadingUserDetail = true;
    try {
      const [resp] = await Promise.all([this.rbacService.getUserPermissions(user._id), this.loadIndividualPermissions()]);
      this.userDetailData = resp?.resp || null;
    } catch (error) {
      console.error('Error loading user permissions:', error);
      alertifyjs.error('Error al cargar permisos del usuario');
    }
    this.loadingUserDetail = false;
  }

  closeUserDetailModal() {
    this.showUserDetailModal = false;
    this.userDetailData = null;
    this.userDetailTarget = null;
  }

  // ── Eliminar Rol ──

  async deleteRole(role: any) {
    if (role.isSystemRole) {
      alertifyjs.error('No se puede eliminar un rol del sistema');
      return;
    }
    alertifyjs.confirm(
      'Eliminar Rol',
      `¿Está seguro de eliminar el rol "${role.name}"? Se desactivarán todas las asignaciones de ${role.usersCount || 0} usuario(s).`,
      async () => {
        try {
          await this.rbacService.deleteRole(role.code);
          alertifyjs.success(`Rol ${role.name} eliminado`);
          await this.loadRoles();
          this.calculateStats();
        } catch (error: any) {
          alertifyjs.error(error?.error?.msg || 'Error al eliminar rol');
        }
      },
      () => {}
    );
  }

  // ── Departamentos ──

  async loadDepartments() {
    this.loadingDepartments = true;
    try {
      const resp = await this.rbacService.getAllDepartments();
      this.departmentsList = resp?.resp || [];
    } catch (error) {
      console.error('Error loading departments:', error);
      this.departmentsList = [];
    }
    this.loadingDepartments = false;
  }

  closeCreateDeptModal() {
    this.showCreateDeptModal = false;
    this.newDepartmentName = '';
  }

  async createDepartment() {
    const name = (this.newDepartmentName || '').trim();
    if (!name) {
      alertifyjs.error('El nombre del departamento es obligatorio');
      return;
    }
    this.savingDepartment = true;
    try {
      await this.rbacService.createDepartment(name);
      alertifyjs.success(`Departamento "${name}" creado`);
      this.closeCreateDeptModal();
      await this.loadDepartments();
    } catch (error: any) {
      alertifyjs.error(error?.error?.msg || 'Error al crear departamento');
    }
    this.savingDepartment = false;
  }

  // ── Asignar rol a departamento ──

  openAssignRoleToDeptModal(dept: any) {
    this.assignRoleToDeptTarget = dept;
    this.assignRoleToDeptCode = '';
    this.showAssignRoleToDeptModal = true;
  }

  closeAssignRoleToDeptModal() {
    this.showAssignRoleToDeptModal = false;
    this.assignRoleToDeptTarget = null;
    this.assignRoleToDeptCode = '';
  }

  getRolesNotInDept(dept: any): any[] {
    const deptRoleCodes = (dept.roles || []).map((r: any) => r.code);
    return this.roles.filter((r: any) => !deptRoleCodes.includes(r.code));
  }

  async assignRoleToDept() {
    if (!this.assignRoleToDeptCode || !this.assignRoleToDeptTarget) return;
    this.savingDepartment = true;
    try {
      await this.rbacService.updateRoleDepartment(this.assignRoleToDeptCode, this.assignRoleToDeptTarget.name);
      alertifyjs.success('Rol asignado al departamento');
      this.closeAssignRoleToDeptModal();
      await Promise.all([this.loadDepartments(), this.loadRoles()]);
    } catch (error: any) {
      alertifyjs.error(error?.error?.msg || 'Error al asignar rol');
    }
    this.savingDepartment = false;
  }

  async removeRoleFromDept(roleCode: string, deptName: string) {
    alertifyjs.confirm(
      'Desasignar Rol',
      `¿Desasignar el rol del departamento "${deptName}"?`,
      async () => {
        try {
          await this.rbacService.updateRoleDepartment(roleCode, null);
          alertifyjs.success('Rol desasignado del departamento');
          await Promise.all([this.loadDepartments(), this.loadRoles()]);
        } catch (error: any) {
          alertifyjs.error(error?.error?.msg || 'Error al desasignar rol');
        }
      },
      () => {}
    );
  }

  // ── Modal de usuarios de un rol ──

  async openRoleUsersModal(role: any, event?: Event) {
    if (event) event.stopPropagation();
    this.roleUsersData = role;
    this.roleUsersList = [];
    this.showRoleUsersModal = true;
    this.loadingRoleUsers = true;
    try {
      const resp = await this.rbacService.getRoleUsers(role.code);
      this.roleUsersList = resp?.resp || [];
    } catch (error) {
      console.error('Error loading role users:', error);
      this.roleUsersList = [];
    }
    this.loadingRoleUsers = false;
  }

  closeRoleUsersModal() {
    this.showRoleUsersModal = false;
    this.roleUsersData = null;
    this.roleUsersList = [];
  }

  onDeleteDepartment(dept: any) {
    alertifyjs.confirm(
      'Eliminar Departamento',
      `¿Está seguro de eliminar el departamento "${dept.name}"?`,
      async () => {
        this.deletingDepartment = dept.name;
        try {
          await this.rbacService.deleteDepartment(dept.name);
          alertifyjs.success(`Departamento "${dept.name}" eliminado`);
          await this.loadDepartments();
        } catch (error: any) {
          alertifyjs.error(error?.error?.msg || 'Error al eliminar departamento');
        }
        this.deletingDepartment = '';
      },
      () => {}
    );
  }

  // ══════════════════════════════════════════
  //  TAB: ROLES
  // ══════════════════════════════════════════

  async openPermissionsModal(role: any) {
    this.selectedRoleForPermissions = role;
    this.showPermissionsModal = true;
    this.loadingPermissions = true;
    try {
      const resp = await this.rbacService.getRolePermissions(role._id);
      this.rolePermissions = resp?.resp || {};
    } catch (error) {
      console.error('Error loading permissions:', error);
      this.rolePermissions = {};
    }
    this.loadingPermissions = false;
  }

  closePermissionsModal() {
    this.showPermissionsModal = false;
    this.selectedRoleForPermissions = null;
    this.rolePermissions = {};
  }

  // ══════════════════════════════════════════
  //  CREAR ROL
  // ══════════════════════════════════════════

  openCreateRoleModal() {
    this.newRole = { code: '', name: '', description: '', department: '', level: 3 };
    this.newRoleExpandedModules = {};
    this.newRolePermissionsMatrix = this.systemModules.map((mod: any) => ({
      module: mod,
      moduleStatus: 'denied',
      submodules: (mod.submodules || []).map((sub: any) => ({
        ...sub,
        subStatus: 'denied',
        actions: sub.actions.map((act: string) => ({
          id: act,
          ...(this.actionLabels[act] || { name: act, icon: 'fas fa-circle', color: 'gray' }),
          enabled: false
        }))
      })),
      sections: (mod.sections || []).map((sec: any) => ({
        ...sec,
        sectionStatus: 'denied',
        submodules: (sec.submodules || []).map((sub: any) => ({
          ...sub,
          subStatus: 'denied',
          actions: sub.actions.map((act: string) => ({
            id: act,
            ...(this.actionLabels[act] || { name: act, icon: 'fas fa-circle', color: 'gray' }),
            enabled: false
          }))
        }))
      }))
    }));
    this.showCreateRoleModal = true;
  }

  closeCreateRoleModal() {
    this.showCreateRoleModal = false;
  }

  onNewRoleNameChange() {
    if (this.newRole.name && !this.newRole.code) {
      this.newRole.code = this.newRole.name
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '');
    }
  }

  toggleNewRoleModuleExpand(moduleId: string) {
    this.newRoleExpandedModules[moduleId] = !this.newRoleExpandedModules[moduleId];
  }

  toggleNewRoleModule(moduleIdx: number) {
    const entry = this.newRolePermissionsMatrix[moduleIdx];
    const newStatus = entry.moduleStatus === 'denied' ? 'all' : 'denied';
    entry.moduleStatus = newStatus;
    entry.submodules.forEach((sub: any) => {
      sub.subStatus = newStatus;
      sub.actions.forEach((act: any) => act.enabled = newStatus === 'all');
    });
    (entry.sections || []).forEach((sec: any) => {
      sec.sectionStatus = newStatus;
      sec.submodules.forEach((sub: any) => {
        sub.subStatus = newStatus;
        sub.actions.forEach((act: any) => act.enabled = newStatus === 'all');
      });
    });
  }

  toggleNewRoleSubmodule(moduleIdx: number, subIdx: number) {
    const sub = this.newRolePermissionsMatrix[moduleIdx].submodules[subIdx];
    const newStatus = sub.subStatus === 'denied' ? 'all' : 'denied';
    sub.subStatus = newStatus;
    sub.actions.forEach((act: any) => act.enabled = newStatus === 'all');
    this.recalcNewRoleModuleStatus(moduleIdx);
  }

  toggleNewRoleAction(moduleIdx: number, subIdx: number, actionIdx: number) {
    const sub = this.newRolePermissionsMatrix[moduleIdx].submodules[subIdx];
    const action = sub.actions[actionIdx];
    action.enabled = !action.enabled;
    const anyEnabled = sub.actions.some((a: any) => a.enabled);
    const allEnabled = sub.actions.every((a: any) => a.enabled);
    sub.subStatus = !anyEnabled ? 'denied' : (allEnabled ? 'all' : 'partial');
    this.recalcNewRoleModuleStatus(moduleIdx);
  }

  recalcNewRoleModuleStatus(moduleIdx: number) {
    const entry = this.newRolePermissionsMatrix[moduleIdx];
    const allSubs = [...entry.submodules, ...(entry.sections || []).flatMap((s: any) => s.submodules)];
    entry.moduleStatus = this._calcGroupStatus(allSubs);
    (entry.sections || []).forEach((sec: any) => {
      sec.sectionStatus = this._calcGroupStatus(sec.submodules);
    });
  }

  toggleNewRoleSection(moduleIdx: number, sectionIdx: number) {
    const sec = this.newRolePermissionsMatrix[moduleIdx].sections[sectionIdx];
    const newStatus = sec.sectionStatus === 'denied' ? 'all' : 'denied';
    sec.sectionStatus = newStatus;
    sec.submodules.forEach((sub: any) => {
      sub.subStatus = newStatus;
      sub.actions.forEach((act: any) => act.enabled = newStatus === 'all');
    });
    this.recalcNewRoleModuleStatus(moduleIdx);
  }

  toggleNewRoleSectionSubmodule(moduleIdx: number, sectionIdx: number, subIdx: number) {
    const sub = this.newRolePermissionsMatrix[moduleIdx].sections[sectionIdx].submodules[subIdx];
    const newStatus = sub.subStatus === 'denied' ? 'all' : 'denied';
    sub.subStatus = newStatus;
    sub.actions.forEach((act: any) => act.enabled = newStatus === 'all');
    this.recalcNewRoleModuleStatus(moduleIdx);
  }

  toggleNewRoleSectionAction(moduleIdx: number, sectionIdx: number, subIdx: number, actionIdx: number) {
    const sub = this.newRolePermissionsMatrix[moduleIdx].sections[sectionIdx].submodules[subIdx];
    const action = sub.actions[actionIdx];
    action.enabled = !action.enabled;
    const anyEnabled = sub.actions.some((a: any) => a.enabled);
    const allEnabled = sub.actions.every((a: any) => a.enabled);
    sub.subStatus = !anyEnabled ? 'denied' : (allEnabled ? 'all' : 'partial');
    this.recalcNewRoleModuleStatus(moduleIdx);
  }

  buildNewRolePermissions(): { module: string; submodule: string; actions: string[] }[] {
    const perms: { module: string; submodule: string; actions: string[] }[] = [];
    for (const entry of this.newRolePermissionsMatrix) {
      for (const sub of entry.submodules) {
        const enabledActions = sub.actions.filter((a: any) => a.enabled).map((a: any) => a.id);
        if (enabledActions.length > 0) {
          perms.push({ module: entry.module.id, submodule: sub.id, actions: enabledActions });
        }
      }
      for (const sec of (entry.sections || [])) {
        for (const sub of sec.submodules) {
          const enabledActions = sub.actions.filter((a: any) => a.enabled).map((a: any) => a.id);
          if (enabledActions.length > 0) {
            perms.push({ module: sec.moduleId, submodule: sub.id, actions: enabledActions });
          }
        }
      }
    }
    return perms;
  }

  async saveNewRole() {
    if (!this.newRole.code || !this.newRole.name || !this.newRole.department) {
      alertifyjs.error('Código, nombre y departamento son obligatorios');
      return;
    }
    this.savingRole = true;
    try {
      const permissions = this.buildNewRolePermissions();
      await this.rbacService.createRole({
        ...this.newRole,
        permissions
      });
      alertifyjs.success(`Rol ${this.newRole.name} creado exitosamente con ${permissions.length} submódulos`);
      this.closeCreateRoleModal();
      await this.loadRoles();
      this.calculateStats();
    } catch (error: any) {
      alertifyjs.error(error?.error?.msg || 'Error al crear rol');
    }
    this.savingRole = false;
  }

  // ══════════════════════════════════════════
  //  TAB: PERMISOS
  // ══════════════════════════════════════════

  loadingMatrix = false;

  expandedSections: any = {};

  toggleSectionExpand(sectionId: string) {
    this.expandedSections[sectionId] = !this.expandedSections[sectionId];
  }

  isSectionExpanded(sectionId: string): boolean {
    return !!this.expandedSections[sectionId];
  }

  _buildSubmodulesFromDB(submodulesDef: any[], moduleId: string, permLookup: any) {
    return submodulesDef.map((sub: any) => {
      const key = `${moduleId}|${sub.id}`;
      const dbActions = permLookup[key] || [];
      const enabledActions = dbActions.filter((a: string) => sub.actions.includes(a));
      const subStatus = enabledActions.length === 0 ? 'denied'
        : enabledActions.length === sub.actions.length ? 'all' : 'partial';
      return {
        ...sub,
        subStatus,
        enabledActions,
        actions: sub.actions.map((act: string) => ({
          id: act,
          ...(this.actionLabels[act] || { name: act, icon: 'fas fa-circle', color: 'gray' }),
          enabled: enabledActions.includes(act)
        }))
      };
    });
  }

  _calcGroupStatus(submodules: any[]) {
    const allDenied = submodules.every((s: any) => s.subStatus === 'denied');
    const allAll = submodules.every((s: any) => s.subStatus === 'all');
    return allDenied ? 'denied' : (allAll ? 'all' : 'partial');
  }

  async onRoleSelectForMatrix() {
    this.matrixDirty = false;
    this.expandedModules = {};
    this.expandedSections = {};
    if (!this.selectedRoleForMatrix) {
      this.permissionsMatrix = [];
      return;
    }

    this.loadingMatrix = true;
    try {
      const resp = await this.rbacService.getRolePermissionsByCode(this.selectedRoleForMatrix);
      const dbPermissions: { module: string; submodule: string; actions: string[] }[] = resp?.resp?.permissions || [];

      const permLookup: { [key: string]: string[] } = {};
      for (const p of dbPermissions) {
        const key = `${p.module}|${p.submodule}`;
        permLookup[key] = p.actions || [];
      }

      this.permissionsMatrix = this.systemModules.map((mod: any) => {
        const submodules = this._buildSubmodulesFromDB(mod.submodules || [], mod.id, permLookup);

        const sections = (mod.sections || []).map((sec: any) => {
          const secSubmodules = this._buildSubmodulesFromDB(sec.submodules || [], sec.moduleId, permLookup);
          return {
            ...sec,
            sectionStatus: this._calcGroupStatus(secSubmodules),
            submodules: secSubmodules
          };
        });

        const allSubs = [...submodules, ...sections.flatMap((s: any) => s.submodules)];
        const moduleStatus = this._calcGroupStatus(allSubs);

        return { module: mod, moduleStatus, submodules, sections };
      });

      console.log(`[Matriz] ${this.selectedRoleForMatrix}: ${dbPermissions.length} submódulos con permisos cargados desde BD`);
    } catch (error) {
      console.error('[Matriz] Error cargando permisos desde BD:', error);
      alertifyjs.error('Error al cargar permisos del rol');
      this.permissionsMatrix = [];
    }
    this.loadingMatrix = false;
  }

  toggleModuleExpand(moduleId: string) {
    this.expandedModules[moduleId] = !this.expandedModules[moduleId];
  }

  isModuleExpanded(moduleId: string): boolean {
    return !!this.expandedModules[moduleId];
  }

  toggleModule(moduleIdx: number) {
    const entry = this.permissionsMatrix[moduleIdx];
    const newStatus = entry.moduleStatus === 'denied' ? 'all' : 'denied';
    entry.moduleStatus = newStatus;
    entry.submodules.forEach((sub: any) => {
      sub.subStatus = newStatus;
      sub.actions.forEach((act: any) => act.enabled = newStatus === 'all');
    });
    (entry.sections || []).forEach((sec: any) => {
      sec.sectionStatus = newStatus;
      sec.submodules.forEach((sub: any) => {
        sub.subStatus = newStatus;
        sub.actions.forEach((act: any) => act.enabled = newStatus === 'all');
      });
    });
    this.matrixDirty = true;
  }

  toggleSubmodule(moduleIdx: number, subIdx: number) {
    const sub = this.permissionsMatrix[moduleIdx].submodules[subIdx];
    const newStatus = sub.subStatus === 'denied' ? 'all' : 'denied';
    sub.subStatus = newStatus;
    sub.actions.forEach((act: any) => act.enabled = newStatus === 'all');
    this.recalcModuleStatus(moduleIdx);
    this.matrixDirty = true;
  }

  toggleSubPermission(moduleIdx: number, subIdx: number, actionIdx: number) {
    const sub = this.permissionsMatrix[moduleIdx].submodules[subIdx];
    const action = sub.actions[actionIdx];
    action.enabled = !action.enabled;
    const anyEnabled = sub.actions.some((a: any) => a.enabled);
    const allEnabled = sub.actions.every((a: any) => a.enabled);
    sub.subStatus = !anyEnabled ? 'denied' : (allEnabled ? 'all' : 'partial');
    this.recalcModuleStatus(moduleIdx);
    this.matrixDirty = true;
  }

  recalcModuleStatus(moduleIdx: number) {
    const entry = this.permissionsMatrix[moduleIdx];
    const allSubs = [...entry.submodules, ...(entry.sections || []).flatMap((s: any) => s.submodules)];
    entry.moduleStatus = this._calcGroupStatus(allSubs);
    (entry.sections || []).forEach((sec: any) => {
      sec.sectionStatus = this._calcGroupStatus(sec.submodules);
    });
  }

  getEnabledSubmodulesCount(moduleEntry: any): number {
    const directCount = moduleEntry.submodules.filter((s: any) => s.subStatus !== 'denied').length;
    const sectionCount = (moduleEntry.sections || []).reduce((acc: number, sec: any) => {
      return acc + sec.submodules.filter((s: any) => s.subStatus !== 'denied').length;
    }, 0);
    return directCount + sectionCount;
  }

  getTotalSubmodulesCount(moduleEntry: any): number {
    const directCount = moduleEntry.submodules.length;
    const sectionCount = (moduleEntry.sections || []).reduce((acc: number, sec: any) => acc + sec.submodules.length, 0);
    return directCount + sectionCount;
  }

  toggleSection(moduleIdx: number, sectionIdx: number) {
    const sec = this.permissionsMatrix[moduleIdx].sections[sectionIdx];
    const newStatus = sec.sectionStatus === 'denied' ? 'all' : 'denied';
    sec.sectionStatus = newStatus;
    sec.submodules.forEach((sub: any) => {
      sub.subStatus = newStatus;
      sub.actions.forEach((act: any) => act.enabled = newStatus === 'all');
    });
    this.recalcModuleStatus(moduleIdx);
    this.matrixDirty = true;
  }

  toggleSectionSubmodule(moduleIdx: number, sectionIdx: number, subIdx: number) {
    const sub = this.permissionsMatrix[moduleIdx].sections[sectionIdx].submodules[subIdx];
    const newStatus = sub.subStatus === 'denied' ? 'all' : 'denied';
    sub.subStatus = newStatus;
    sub.actions.forEach((act: any) => act.enabled = newStatus === 'all');
    this.recalcModuleStatus(moduleIdx);
    this.matrixDirty = true;
  }

  toggleSectionSubPermission(moduleIdx: number, sectionIdx: number, subIdx: number, actionIdx: number) {
    const sub = this.permissionsMatrix[moduleIdx].sections[sectionIdx].submodules[subIdx];
    const action = sub.actions[actionIdx];
    action.enabled = !action.enabled;
    const anyEnabled = sub.actions.some((a: any) => a.enabled);
    const allEnabled = sub.actions.every((a: any) => a.enabled);
    sub.subStatus = !anyEnabled ? 'denied' : (allEnabled ? 'all' : 'partial');
    this.recalcModuleStatus(moduleIdx);
    this.matrixDirty = true;
  }

  getEnabledSectionSubmodulesCount(section: any): number {
    return section.submodules.filter((s: any) => s.subStatus !== 'denied').length;
  }

  getEnabledActionsInSub(sub: any): number {
    return sub.actions.filter((a: any) => a.enabled).length;
  }

  buildMatrixPermissions(): { module: string; submodule: string; actions: string[] }[] {
    const perms: { module: string; submodule: string; actions: string[] }[] = [];
    for (const entry of this.permissionsMatrix) {
      for (const sub of entry.submodules) {
        const enabledActions = sub.actions.filter((a: any) => a.enabled).map((a: any) => a.id);
        if (enabledActions.length > 0) {
          perms.push({ module: entry.module.id, submodule: sub.id, actions: enabledActions });
        }
      }
      for (const sec of (entry.sections || [])) {
        for (const sub of sec.submodules) {
          const enabledActions = sub.actions.filter((a: any) => a.enabled).map((a: any) => a.id);
          if (enabledActions.length > 0) {
            perms.push({ module: sec.moduleId, submodule: sub.id, actions: enabledActions });
          }
        }
      }
    }
    return perms;
  }

  async saveMatrixPermissions() {
    if (!this.selectedRoleForMatrix) return;
    this.savingMatrix = true;
    try {
      const permissions = this.buildMatrixPermissions();
      const resp = await this.rbacService.updateRolePermissions(this.selectedRoleForMatrix, permissions);
      this.syncRolePermissionsMapFromMatrix();
      alertifyjs.success(`Permisos guardados: ${resp?.resp?.newCount || permissions.length} permisos para ${this.selectedRoleForMatrix}`);
      this.matrixDirty = false;
    } catch (error: any) {
      alertifyjs.error(error?.error?.msg || 'Error al guardar permisos');
    }
    this.savingMatrix = false;
  }

  syncRolePermissionsMapFromMatrix() {
    if (!this.selectedRoleForMatrix) return;
    const roleEntry: any = {};
    for (const entry of this.permissionsMatrix) {
      const modId = entry.module.id;
      const subMap: any = {};
      for (const sub of entry.submodules) {
        if (sub.subStatus === 'denied') {
          subMap[sub.id] = 'denied';
        } else if (sub.subStatus === 'all') {
          subMap[sub.id] = 'all';
        } else {
          subMap[sub.id] = sub.actions.filter((a: any) => a.enabled).map((a: any) => a.id);
        }
      }
      roleEntry[modId] = Object.keys(subMap).length > 0 ? subMap : 'denied';
      for (const sec of (entry.sections || [])) {
        const secSubMap: any = {};
        for (const sub of sec.submodules) {
          if (sub.subStatus === 'denied') {
            secSubMap[sub.id] = 'denied';
          } else if (sub.subStatus === 'all') {
            secSubMap[sub.id] = 'all';
          } else {
            secSubMap[sub.id] = sub.actions.filter((a: any) => a.enabled).map((a: any) => a.id);
          }
        }
        roleEntry[sec.moduleId] = Object.keys(secSubMap).length > 0 ? secSubMap : 'denied';
      }
    }
    this.rolePermissionsMap[this.selectedRoleForMatrix] = roleEntry;
  }

  // ══════════════════════════════════════════
  //  TAB: AUDITORÍA
  // ══════════════════════════════════════════

  // Audit logs are now loaded from backend via loadAuditLogs()

  // ══════════════════════════════════════════
  //  HELPERS
  // ══════════════════════════════════════════

  getDepartmentColor(department: string): string {
    const colors: any = {
      'Crédito y Cobranza': 'bg-blue-100 text-blue-800',
      'Crédito': 'bg-indigo-100 text-indigo-800',
      'Cobranza': 'bg-orange-100 text-orange-800',
      'QA': 'bg-purple-100 text-purple-800',
      'Atención al Cliente': 'bg-green-100 text-green-800',
      'Contabilidad': 'bg-yellow-100 text-yellow-800',
      'Recursos Humanos': 'bg-pink-100 text-pink-800',
    };
    return colors[department] || 'bg-gray-100 text-gray-800';
  }

  getDepartmentBgColor(department: string): string {
    const colors: any = {
      'Crédito y Cobranza': 'from-blue-500 to-blue-600',
      'Crédito': 'from-indigo-500 to-indigo-600',
      'Cobranza': 'from-orange-500 to-orange-600',
      'QA': 'from-purple-500 to-purple-600',
      'Atención al Cliente': 'from-green-500 to-green-600',
      'Contabilidad': 'from-yellow-500 to-yellow-600',
      'Recursos Humanos': 'from-pink-500 to-pink-600',
    };
    return colors[department] || 'from-gray-500 to-gray-600';
  }

  getLevelLabel(level: number): string {
    const labels: any = {
      7: 'Gerencia',
      6: 'Dirección',
      5: 'Coordinación',
      4: 'Liderazgo',
      3: 'Senior',
      2: 'Analista',
      1: 'Junior'
    };
    return labels[level] || `Nivel ${level}`;
  }

  getActionLabel(action: string): { name: string; icon: string; color: string } {
    const labels: any = {
      'read': { name: 'Leer', icon: 'fas fa-eye', color: 'blue' },
      'create': { name: 'Crear', icon: 'fas fa-plus', color: 'green' },
      'update': { name: 'Editar', icon: 'fas fa-edit', color: 'yellow' },
      'delete': { name: 'Eliminar', icon: 'fas fa-trash', color: 'red' },
      'approve': { name: 'Aprobar', icon: 'fas fa-check-circle', color: 'purple' },
      'execute': { name: 'Ejecutar', icon: 'fas fa-play', color: 'indigo' },
      'generate': { name: 'Generar', icon: 'fas fa-file-export', color: 'teal' },
      'export': { name: 'Exportar', icon: 'fas fa-download', color: 'cyan' },
      'history': { name: 'Historial', icon: 'fas fa-history', color: 'gray' },
      'manage': { name: 'Gestionar', icon: 'fas fa-cogs', color: 'orange' },
    };
    return labels[action] || this.actionLabels?.[action] || { name: action, icon: 'fas fa-circle', color: 'gray' };
  }

  getActionColorClass(color: string): string {
    const classes: any = {
      'blue': 'bg-blue-100 text-blue-700 border border-blue-200',
      'green': 'bg-green-100 text-green-700 border border-green-200',
      'yellow': 'bg-yellow-100 text-yellow-700 border border-yellow-200',
      'red': 'bg-red-100 text-red-700 border border-red-200',
      'purple': 'bg-purple-100 text-purple-700 border border-purple-200',
      'indigo': 'bg-indigo-100 text-indigo-700 border border-indigo-200',
      'teal': 'bg-teal-100 text-teal-700 border border-teal-200',
      'cyan': 'bg-cyan-100 text-cyan-700 border border-cyan-200',
      'gray': 'bg-gray-100 text-gray-700 border border-gray-200',
      'orange': 'bg-orange-100 text-orange-700 border border-orange-200',
    };
    return classes[color] || 'bg-gray-100 text-gray-700 border border-gray-200';
  }

  getScopeLabel(scope: string): string {
    const labels: any = {
      'all': 'Todos',
      'department': 'Departamento',
      'own': 'Propios',
      'none': 'Ninguno'
    };
    return labels[scope] || scope;
  }

  getScopeColor(scope: string): string {
    const colors: any = {
      'all': 'bg-green-100 text-green-800',
      'department': 'bg-blue-100 text-blue-800',
      'own': 'bg-yellow-100 text-yellow-800',
      'none': 'bg-red-100 text-red-800'
    };
    return colors[scope] || 'bg-gray-100 text-gray-800';
  }

  groupPermissionsByModule(permissions: any[]): any[] {
    const groups: any = {};
    permissions.forEach(p => {
      const code = p.code || p;
      const parts = code.split('.');
      const mod = parts[0] || 'general';
      if (!groups[mod]) {
        groups[mod] = { module: mod, permissions: [] };
      }
      groups[mod].permissions.push(code);
    });
    return Object.values(groups);
  }

  getObjectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  getRolesByDepartment(): any[] {
    const depts: any = {};
    this.roles.forEach(r => {
      const dept = r.department || 'Sin departamento';
      if (!depts[dept]) {
        depts[dept] = { department: dept, roles: [], color: this.getDepartmentBgColor(dept) };
      }
      depts[dept].roles.push(r);
    });
    return Object.values(depts);
  }
}
